import { wrapIslandTree } from './wrap-island-tree';

/**
 * @typedef {object} MountIslandsDependencies
 * @property {(type: import('react').ComponentType<any>, props: Record<string, unknown>) => import('react').ReactElement} createElement
 * @property {(container: Element) => { render: (element: import('react').ReactElement) => void }} createRoot
 * @property {(container: Element, element: import('react').ReactElement) => void} hydrateRoot
 * @property {(islandId: string) => Promise<{
 *   component: import('react').ComponentType<any>,
 *   prepareMount: ((context: {
 *     mountElement: Element,
 *     props: Record<string, unknown>,
 *   }) => {
 *     confirmBeforeHydrate?: () => boolean,
 *     forceClientRender?: boolean,
 *     props?: Record<string, unknown>,
 *   } | undefined) | null,
 * } | null>} loadIslandModule
 * @property {(propsId: string | undefined) => Record<string, unknown>} readProps
 * @property {(mount: () => Promise<void>) => void} [scheduleIdle] Defer mount to browser idle time.
 * @property {(element: Element, mount: () => Promise<void>) => void} [observeVisible] Mount when the element scrolls into view.
 * @property {typeof wrapIslandTree} [wrapIslandTree] Wrap island component for Emotion + Revolucci (injected in tests).
 */

/**
 * True when PHP emitted server-rendered markup inside the mount node. Ignores whitespace-only text so
 * template indentation does not look like SSR content. Drives hydrate-vs-client-render.
 * @param {Element} element
 * @returns {boolean}
 */
function hasServerRenderedMarkup(element) {
	if (element.childElementCount > 0) {
		return true;
	}

	return element.textContent.trim() !== '';
}

/**
 * @param {Record<string, unknown>} islandProps
 * @param {Element} mountElement
 * @param {((context: {
 *   mountElement: Element,
 *   props: Record<string, unknown>,
 * }) => {
 *   confirmBeforeHydrate?: () => boolean,
 *   forceClientRender?: boolean,
 *   props?: Record<string, unknown>,
 * } | undefined) | null} prepareMount
 * @returns {{
 *   confirmBeforeHydrate: (() => boolean) | null,
 *   forceClientRender: boolean,
 *   props: Record<string, unknown>,
 * }}
 */
function applyPrepareMount(islandProps, mountElement, prepareMount) {
	if (prepareMount === null) {
		return { confirmBeforeHydrate: null, forceClientRender: false, props: islandProps };
	}

	const prepareResult = prepareMount({ props: islandProps, mountElement });
	if (prepareResult === undefined) {
		return { confirmBeforeHydrate: null, forceClientRender: false, props: islandProps };
	}

	const confirmBeforeHydrate = typeof prepareResult.confirmBeforeHydrate === 'function'
		? prepareResult.confirmBeforeHydrate
		: null;

	return {
		confirmBeforeHydrate,
		forceClientRender: prepareResult.forceClientRender === true,
		props: prepareResult.props ?? islandProps,
	};
}

/**
 * Defers a mount to browser idle time, falling back to a near-immediate timeout where
 * requestIdleCallback is unavailable (Safari, jsdom).
 * @param {() => Promise<void>} mount
 * @returns {void}
 */
function defaultScheduleIdle(mount) {
	const run = () => {
		mount().catch((mountError) => console.warn('react-islands: idle mount failed', mountError));
	};

	if (typeof globalThis.requestIdleCallback === 'function') {
		globalThis.requestIdleCallback(run);
		return;
	}

	globalThis.setTimeout(run, 1);
}

/**
 * The mount wrapper uses `display:contents`, which generates no layout box — IntersectionObserver
 * cannot observe such an element, so fall back to its parent (the per-module wrapper, which has a
 * box). Visibility of that wrapper tracks the island since each module renders in its own wrapper.
 * @param {Element} element
 * @returns {Element}
 */
function resolveObservableTarget(element) {
	const isContents = globalThis.getComputedStyle(element).display === 'contents';
	if (isContents && element.parentElement !== null) {
		return element.parentElement;
	}

	return element;
}

/**
 * Mounts the element the first time it intersects the viewport, falling back to an immediate mount
 * where IntersectionObserver is unavailable (jsdom).
 * @param {Element} element
 * @param {() => Promise<void>} mount
 * @returns {void}
 */
export function defaultObserveVisible(element, mount) {
	const run = () => {
		mount().catch((mountError) => console.warn('react-islands: visible mount failed', mountError));
	};

	if (typeof globalThis.IntersectionObserver !== 'function') {
		run();
		return;
	}

	const observer = new globalThis.IntersectionObserver((entries) => {
		const isVisible = entries.some((entry) => entry.isIntersecting);
		if (isVisible) {
			observer.disconnect();
			run();
		}
	});
	observer.observe(resolveObservableTarget(element));
}

/**
 * Loads an island component and hydrates the SSR markup or client-renders it. Resolves once the
 * single island is mounted; load failures are swallowed with a warning so other islands continue.
 * @param {Element} mountElement
 * @param {string} islandId
 * @param {MountIslandsDependencies} dependencies
 * @returns {Promise<void>}
 */
async function mountIsland(mountElement, islandId, dependencies) {
	const { createElement, createRoot, hydrateRoot, loadIslandModule, readProps } = dependencies;

	let loadedIsland;
	try {
		loadedIsland = await loadIslandModule(islandId);
	} catch (loadError) {
		console.warn('react-islands: failed to load island', islandId, loadError);
		return;
	}

	if (loadedIsland === null) {
		return;
	}

	const { component: Component, prepareMount } = loadedIsland;

	const instanceId = mountElement.dataset.reactIslandPropsId;
	if (typeof instanceId !== 'string' || instanceId === '') {
		console.warn('react-islands: missing data-react-island-props-id for island', islandId);
		return;
	}

	const readIslandProps = readProps(instanceId);
	const { confirmBeforeHydrate, forceClientRender, props: islandProps } = applyPrepareMount(
		readIslandProps,
		mountElement,
		prepareMount,
	);

	const wrap = dependencies.wrapIslandTree ?? wrapIslandTree;
	const element = wrap(createElement, Component, islandProps, mountElement);
	const clientOnly = mountElement.hasAttribute('data-react-island-client-only');
	let shouldClientRender = clientOnly
		|| !hasServerRenderedMarkup(mountElement)
		|| forceClientRender;

	if (!shouldClientRender && confirmBeforeHydrate !== null && confirmBeforeHydrate()) {
		shouldClientRender = true;
	}

	// Hydrating an empty node mismatches React's SSR expectations, so only hydrate when PHP emitted markup.
	if (shouldClientRender) {
		createRoot(mountElement).render(element);
		return;
	}

	hydrateRoot(mountElement, element);
}

/**
 * Mount every `[data-react-island-root]` element, scheduling each by its hydration tier:
 * `load` mounts immediately, `idle` waits for browser idle, `visible` waits until it scrolls into
 * view. The `none` tier never emits a root (PHP renders it as static markup), so it is never seen
 * here. Dependencies are injected for testability.
 * @param {MountIslandsDependencies} dependencies
 * @returns {Promise<void>}
 */
export async function mountIslands(dependencies) {
	const scheduleIdle = dependencies.scheduleIdle ?? defaultScheduleIdle;
	const observeVisible = dependencies.observeVisible ?? defaultObserveVisible;
	const mountElements = document.querySelectorAll('[data-react-island-root]');

	const immediateMounts = [];
	for (const mountElement of mountElements) {
		const islandId = mountElement.dataset.reactIsland;
		if (typeof islandId !== 'string' || islandId === '') {
			continue;
		}

		const tier = mountElement.dataset.reactIslandHydration ?? 'load';
		const mount = () => mountIsland(mountElement, islandId, dependencies);

		if (tier === 'visible') {
			observeVisible(mountElement, mount);
		} else if (tier === 'idle') {
			scheduleIdle(mount);
		} else {
			immediateMounts.push(mount());
		}
	}

	await Promise.all(immediateMounts);
}
